import { useEffect } from "react";
import { useImmer } from "use-immer";

import type {
  AgenticLogicRuleDto,
  AgenticLogicRuleSettings,
  AgenticLogicTemplateSummaryDto,
} from "@intelligems/ig-types";
import { AGENTIC_LOGIC_DEFAULT_RULE_SETTINGS } from "@intelligems/ig-types";

import { sameRuleSettings } from "../workspace/ruleSettings";

import {
  formatTemplateConfigJson,
  parseTemplateConfigJson,
  sameTemplateParams,
} from "./templateConfigJson";

export type TemplateConfigWorkspaceMode =
  | { kind: "create"; template: AgenticLogicTemplateSummaryDto }
  | { kind: "edit"; rule: AgenticLogicRuleDto };

/**
 * No `templateVersion`: the save endpoint validates the params against the
 * live template and stamps its version server-side, so the client never
 * asserts a version it did not resolve.
 */
export type PersistTemplateConfigInput =
  | {
      kind: "create";
      name: string;
      templateId: string;
      templateParams: Record<string, unknown>;
      settings: AgenticLogicRuleSettings;
    }
  | {
      kind: "edit";
      ruleId: string;
      name: string;
      templateParams: Record<string, unknown>;
      settings: AgenticLogicRuleSettings;
    };

export interface TemplateConfigWorkspaceAdapter {
  persist: (input: PersistTemplateConfigInput) => Promise<AgenticLogicRuleDto>;
}

interface WorkspaceState {
  name: string;
  source: string;
  /**
   * The rule's recommendation settings, edited on the Display settings tab.
   * They live in the SAME buffer as the configuration because a template rule
   * has one Save: the four answers and the params are one setup, and splitting
   * them would let a create screen persist a rule whose settings were never
   * asked for.
   */
  settings: AgenticLogicRuleSettings;
  baselineName: string;
  baselineSource: string;
  baselineSettings: AgenticLogicRuleSettings;
  /**
   * The params the baseline text was formatted from — the grounding point the
   * server copy is compared against. Text alone cannot serve: a round trip that
   * reorders keys re-formats the same configuration.
   */
  baselineParams: Record<string, unknown>;
  /**
   * Params written to the rule outside this workspace while the buffer held
   * unsaved edits. Non-null means Save is held until the merchant picks a side.
   */
  conflictParams: Record<string, unknown> | null;
  error: string | null;
  isPersisting: boolean;
}

const initialTemplateId = (mode: TemplateConfigWorkspaceMode): string => {
  const templateId =
    mode.kind === "create" ? mode.template.id : mode.rule.templateId;
  if (!templateId) {
    throw new Error("Builder rule is missing its template id");
  }
  return templateId;
};

const initialParams = (
  mode: TemplateConfigWorkspaceMode
): Record<string, unknown> =>
  mode.kind === "create"
    ? mode.template.exampleParams
    : (mode.rule.templateParams ?? {});

const initialSettings = (
  mode: TemplateConfigWorkspaceMode
): AgenticLogicRuleSettings =>
  mode.kind === "edit"
    ? mode.rule.settings
    : AGENTIC_LOGIC_DEFAULT_RULE_SETTINGS;

/**
 * A detached copy of server params before they enter workspace state. Immer
 * auto-freezes whatever it stores, and the object handed in here belongs to the
 * query cache — adopting it directly would freeze the cache's copy.
 */
const snapshotParams = (
  params: Record<string, unknown>
): Record<string, unknown> =>
  JSON.parse(JSON.stringify(params)) as Record<string, unknown>;

export const useTemplateConfigWorkspace = ({
  mode,
  adapter,
}: {
  mode: TemplateConfigWorkspaceMode;
  adapter: TemplateConfigWorkspaceAdapter;
}) => {
  const templateId = initialTemplateId(mode);
  const startingName =
    mode.kind === "create" ? mode.template.title : mode.rule.name;
  const startingParams = initialParams(mode);
  const startingSource = formatTemplateConfigJson(startingParams);
  const startingSettings = initialSettings(mode);
  const [state, setState] = useImmer<WorkspaceState>({
    name: startingName,
    source: startingSource,
    settings: startingSettings,
    baselineName: startingName,
    baselineSource: startingSource,
    baselineSettings: startingSettings,
    baselineParams: startingParams,
    conflictParams: null,
    error: null,
    isPersisting: false,
  });

  const setName = (name: string) =>
    setState((draft) => void (draft.name = name));
  const setSource = (source: string) =>
    setState((draft) => {
      draft.source = source;
      draft.error = null;
    });
  /** Replaces the settings WHOLE — never merged per key. See the DTO's note. */
  const setSettings = (settings: AgenticLogicRuleSettings) =>
    setState((draft) => void (draft.settings = settings));

  /**
   * Adopt a name that was persisted OUTSIDE this workspace — the rule detail
   * page renames through the page frame, and without rebasing the baseline
   * here the next "Save changes" would write the stale mounted name back over
   * it.
   */
  const syncPersistedName = (name: string) =>
    setState((draft) => {
      draft.name = name;
      draft.baselineName = name;
    });

  // Keyed on the DTO's own reference, never a `?? {}` coalesce: a fresh empty
  // object every render would re-run the effect on every render.
  const serverParams =
    mode.kind === "edit" ? mode.rule.templateParams : undefined;
  const serverName = mode.kind === "edit" ? mode.rule.name : undefined;
  const serverSettings = mode.kind === "edit" ? mode.rule.settings : undefined;

  /**
   * Keep the buffer a VIEW of the rule's persisted configuration.
   *
   * `useImmer`'s argument is an initial value, so without this the buffer stays
   * the snapshot taken when the page first mounted. The Atlas chat writes
   * `templateParams` server-side and invalidates the rule query, so the rule
   * prop changes underneath a mounted workspace — and Save, a blind PUT of the
   * buffer, would republish the params the merchant never saw over it.
   *
   * Clean buffer ⇒ adopt. Dirty buffer ⇒ disclose and hold Save; unsaved edits
   * are never discarded silently and an out-of-band write is never reverted
   * silently. Compared and assigned inside the immer updater
   * (read-at-call-time), mirroring `useCodegenWorkspace`'s settle effect, so a
   * keystroke racing the refetch cannot be lost.
   */
  useEffect(() => {
    if (
      serverParams === undefined ||
      serverName === undefined ||
      serverSettings === undefined
    ) {
      return;
    }
    const nextParams = serverParams ?? {};
    setState((draft) => {
      if (draft.name === draft.baselineName && draft.name !== serverName) {
        draft.name = serverName;
        draft.baselineName = serverName;
      }

      // Settings adopt on the same terms as the name: an untouched field takes
      // the server's word, an edited one is never overwritten under the author.
      if (
        sameRuleSettings(draft.settings, draft.baselineSettings) &&
        !sameRuleSettings(draft.settings, serverSettings)
      ) {
        draft.settings = serverSettings;
        draft.baselineSettings = serverSettings;
      }

      if (sameTemplateParams(nextParams, draft.baselineParams)) {
        return;
      }
      if (draft.source === draft.baselineSource) {
        const adopted = formatTemplateConfigJson(nextParams);
        draft.source = adopted;
        draft.baselineSource = adopted;
        draft.baselineParams = snapshotParams(nextParams);
        draft.conflictParams = null;
      } else {
        draft.conflictParams = snapshotParams(nextParams);
      }
    });
  }, [serverParams, serverName, serverSettings, setState]);

  /**
   * The merchant's answer to the conflict. `take_theirs` drops the local edit
   * they chose to drop; `keep_mine` moves the grounding point onto the server
   * copy WITHOUT touching the buffer, so the Save that follows is a deliberate,
   * disclosed overwrite rather than the silent one this whole effect exists to
   * prevent.
   */
  const resolveParamsConflict = (choice: "take_theirs" | "keep_mine") => {
    const theirs = state.conflictParams;
    if (theirs === null) {
      return;
    }
    setState((draft) => {
      if (choice === "take_theirs") {
        const adopted = formatTemplateConfigJson(theirs);
        draft.source = adopted;
        draft.baselineSource = adopted;
        draft.error = null;
      }
      draft.baselineParams = theirs;
      draft.conflictParams = null;
    });
  };

  /**
   * BUFFER dirtiness only. The exit guard and `resolveRuleExitMode` want
   * exactly this meaning — unsaved keystrokes — so it stays exported, but it is
   * not a Save term.
   */
  const isDirty =
    state.name !== state.baselineName ||
    state.source !== state.baselineSource ||
    !sameRuleSettings(state.settings, state.baselineSettings);
  const isSourceValid = parseTemplateConfigJson(state.source).valid;
  const hasName = state.name.trim().length > 0;
  // Deliberately NOT gated on `isDirty`: see
  // agenticCodegen/workspace/saveAvailability.ts. Recommendation settings
  // autosave onto the rule row and only deploy at the next publish, so a clean
  // buffer is exactly when the merchant still needs to press Save. Re-saving an
  // unchanged config is a well-defined, idempotent republish.
  //
  // Gated on the conflict, though: Save publishes the buffer, and while a
  // competing write is undisclosed the buffer is not the merchant's answer to
  // it. Held, not resolved for them.
  const hasParamsConflict = state.conflictParams !== null;
  const canPersist = hasName && isSourceValid && !hasParamsConflict;

  const persist = async (): Promise<AgenticLogicRuleDto | null> => {
    // Not gated on `canPersist` wholesale: an unparsable buffer must still
    // write its parse message into `error` rather than no-opping silently at
    // a merchant who pressed Save. An undisclosed conflict DOES stop here:
    // the banner already names the problem and the two ways out.
    if (!hasName || hasParamsConflict || state.isPersisting) {
      return null;
    }
    setState((draft) => {
      draft.error = null;
      draft.isPersisting = true;
    });
    try {
      const parsed = parseTemplateConfigJson(state.source);
      if (!parsed.valid) {
        setState((draft) => void (draft.error = parsed.message));
        return null;
      }

      // The save endpoint is the validator: it rejects params the template
      // cannot run (a 400 whose message lands in `error` below) and stamps the
      // live template version, so there is no separate validate round trip.
      const name = state.name.trim();
      const rule = await adapter.persist(
        mode.kind === "create"
          ? {
              kind: "create",
              name,
              templateId,
              templateParams: parsed.value,
              settings: state.settings,
            }
          : {
              kind: "edit",
              ruleId: mode.rule.id,
              name,
              templateParams: parsed.value,
              settings: state.settings,
            }
      );
      // The stored row is the new grounding point, so the buffer settles on
      // the params the server echoed back rather than the local parse.
      const savedParams = rule.templateParams ?? parsed.value;
      const savedSource = formatTemplateConfigJson(savedParams);
      setState((draft) => {
        draft.name = name;
        draft.source = savedSource;
        draft.baselineName = name;
        draft.baselineSource = savedSource;
        draft.baselineSettings = draft.settings;
        draft.baselineParams = snapshotParams(savedParams);
      });
      return rule;
    } catch (error) {
      setState((draft) => {
        draft.error =
          error instanceof Error ? error.message : "Rule could not be saved.";
      });
      return null;
    } finally {
      setState((draft) => void (draft.isPersisting = false));
    }
  };

  return {
    ...state,
    templateId,
    canPersist,
    hasParamsConflict,
    resolveParamsConflict,
    isDirty,
    isSourceValid,
    setName,
    syncPersistedName,
    setSource,
    setSettings,
    persist,
  };
};
